import React from 'react';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';
import { Button, Money, cx, focusRing } from '@/components/ui';

interface OrderSummaryFooterProps {
  totalAmount: number;
  onClearOrder: () => void;
  onProceedToFunding: () => void;
  onShowOrderSummary?: () => void;
  className?: string;
}

/**
 * Cart bar under the game list: clear the order, open the order summary, proceed to funding.
 * "Proceed to funding" is the screen's one emphasised money action.
 */
const OrderSummaryFooter: React.FC<OrderSummaryFooterProps> = ({
  totalAmount,
  onClearOrder,
  onProceedToFunding,
  onShowOrderSummary,
  className,
}) => {
  const hasOrder = totalAmount > 0;

  return (
    <footer className={cx('w-full', className)}>
      <div className="border-t border-edge-subtle bg-chrome backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-[var(--wf-page-2xl)] flex-row items-center justify-between gap-2 px-3 @bp-sm:px-4 @bp-md:gap-3 @bp-md:px-6 @bp-lg:gap-5 @bp-lg:px-16 @bp-xl:px-20">
          {/* Left: Clear order + order summary */}
          <div className="flex min-w-0 flex-1 flex-row items-center gap-2 @bp-md:gap-3 @bp-lg:gap-5 @bp-xl:gap-6">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (hasOrder) onClearOrder();
              }}
              disabled={!hasOrder}
              aria-label="Clear order"
              icon={<RotateCcw />}
              className="shrink-0"
            >
              Clear Order
            </Button>

            <button
              type="button"
              disabled={!onShowOrderSummary}
              onClick={() => {
                if (onShowOrderSummary) {
                  playClickSound();
                  onShowOrderSummary();
                }
              }}
              className={cx(
                'flex min-w-0 flex-col items-start rounded-control border-0 bg-transparent px-1 py-0.5 text-left transition-opacity hover:opacity-80',
                focusRing
              )}
            >
              <span className="block truncate font-ui text-small font-semibold text-link underline">Order Summary</span>
              <Money amount={Math.round(totalAmount)} direction="in" size="title" />
            </button>
          </div>

          {/* Right: Proceed to funding */}
          <Button
            variant="primary"
            size="sm"
            emphasis
            onClick={() => {
              if (hasOrder) onProceedToFunding();
            }}
            disabled={!hasOrder}
            iconRight={<ArrowRight />}
            className="shrink-0"
          >
            <span className="hidden @bp-sm:inline">Proceed to funding</span>
            <span className="@bp-sm:hidden">Funding</span>
          </Button>
        </div>
        <div className="h-[env(safe-area-inset-bottom,0px)]" />
      </div>
    </footer>
  );
};

export default OrderSummaryFooter;
